import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Button, Grid, IconButton, TextField, Typography, TablePagination } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import SearchField from '../../components/input/SearchField';
import Layout from '../../components/Layout/Layout';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import Edit from './Edit';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchProductData } from '../../redux/slices/productSlice';
import { useReactToPrint } from 'react-to-print';
import ProductPrint from '../../print/ProductPrint';

export default function Product() {
    let { user } = useSelector((state) => state?.user);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);

    const [product, setProduct] = useState(null);
    const [loadReq, setLoadReq] = useState({ type: null, load: false });
    const [filteredData, setFilteredData] = useState(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Number of items per page

    const dispatch = useDispatch();
    const { data, isLoading, error } = useSelector((state) => state.product);

    React.useEffect(() => {
        if (!data) {
            dispatch(fetchProductData());
        } else {
            setFilteredData(data); // Initialize filtered data with all data
        }
    }, [data]);

    const handleSearch = (searchText) => {
        // Filter data based on search text in name, category, and subCategory
        const filteredResults = data.filter((row) => {
            const searchLower = searchText?.toLowerCase();
            return (
                row?.name?.toLowerCase().includes(searchLower) || row?.category?.toLowerCase().includes(searchLower)
                // || row?.subCategory?.toLowerCase().includes(searchLower)
            );
        });
        setFilteredData(filteredResults);
    };

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `products/${product?.id}`, {});
            if (res?.data) {
                setProduct(null);
                dispatch(fetchProductData());
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    const handleInputChange = (id, key, value) => {
        setFilteredData((prevItems) => prevItems.map((item) => (item.id === id ? { ...item, [key]: value } : item)));
    };

    const handleBulkUpdate = async () => {
        setLoadReq({ type: 'bulk', load: true });
        try {
            const res = await apiRequest('put', `products/update/bulk`, filteredData);
            if (res?.data) {
                dispatch(fetchProductData());
                return toast.success('Quantity updated successfully');
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadReq({ type: 'bulk', load: false });
        }
    };

    const handleReset = async () => {
        let resetData = null;
        if (user?.name == 'Data Entry 1') {
            resetData = data.map((item) => ({ ...item, availableQuantity: 0 }));
        } else if (user?.name == 'Data Entry 2') {
            resetData = data.map((item) => ({ ...item, rawMaterialQuantity: 0 }));
        } else if (user?.name == 'Shauqat -') {
            resetData = data.map((item) => ({ ...item, machines: 0 }));
        } else {
            resetData = data.map((item) => ({ ...item, availableQuantity: 0, rawMaterialQuantity: 0 }));
        }

        setLoadReq({ type: 'reset', load: true });
        try {
            const res = await apiRequest('put', `products/update/bulk`, resetData);
            if (res?.data) {
                dispatch(fetchProductData());
                return toast.success('Quantity reset successfully');
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadReq({ type: 'reset', load: false });
        }
    };

    // Handle change in pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    // Paginate the filtered data
    const paginatedData = filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const componentRef = React.useRef(null);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    return (
        <Layout>
            <>
                <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                    <Grid item lg={5} sm={3.5} md={6.5} xs={6}>
                        <Typography variant="h6">Products</Typography>
                    </Grid>

                    {user?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Product
                            </Button>
                        </Grid>
                    )}
                    <Grid item lg={1} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                        <Button onClick={handlePrint} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                            Print
                        </Button>
                    </Grid>
                    <Grid item lg={1.5} sm={5} md={3} xs={12}>
                        {user?.role === 'data-entry' && (
                            <Button
                                disabled={loadReq?.load && loadReq?.type === 'bulk'}
                                onClick={handleBulkUpdate}
                                fullWidth
                                variant="contained"
                            >
                                Update All
                            </Button>
                        )}
                    </Grid>
                    {/* <Grid item lg={1} sm={5} md={3} xs={12}>
                        <Button onClick={handleReset} fullWidth variant="contained">
                            Reset
                        </Button>
                    </Grid> */}
                    <Grid item lg={3} sm={5} md={3} xs={12}>
                        <SearchField onChange={(e) => handleSearch(e?.target?.value)} label={'Search Products'} />
                    </Grid>

                    {user?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Product
                            </Button>
                        </Grid>
                    )}
                    <Grid item lg={1} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Button onClick={handlePrint} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                            Print
                        </Button>
                    </Grid>
                </Grid>

                {isLoading && <Loader />}

                {!!filteredData && (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Packing</TableCell>
                                    {user?.role === 'super-admin' && <TableCell>Price</TableCell>}
                                    <TableCell>Category</TableCell>
                                    <TableCell>Machines</TableCell>
                                    <TableCell>Available Quantity</TableCell>
                                    <TableCell>Raw Material Quantity</TableCell>

                                    {user?.role == 'super-admin' && (
                                        <>
                                            <TableCell sx={{ width: '60px' }} />
                                            <TableCell sx={{ width: '60px' }} />
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData?.map((row) => (
                                    <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                            {row.serialNo}
                                        </TableCell>
                                        <TableCell>{row?.name}</TableCell>
                                        <TableCell>{row?.packing}</TableCell>
                                        {user?.role === 'super-admin' && <TableCell>{row?.price}</TableCell>}
                                        <TableCell>{row?.category}</TableCell>
                                        {user?.role === 'data-entry' ? (
                                            <>
                                                {user?.name == 'Shauqat -' ? (
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={row?.machines}
                                                                onChange={(e) => handleInputChange(row.id, 'machines', e.target.value)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{row?.availableQuantity}</TableCell>
                                                        <TableCell>{row?.rawMaterialQuantity}</TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>{row?.machines}-</TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={row?.availableQuantity}
                                                                onChange={(e) =>
                                                                    handleInputChange(row.id, 'availableQuantity', e.target.value)
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={row?.rawMaterialQuantity}
                                                                onChange={(e) =>
                                                                    handleInputChange(row.id, 'rawMaterialQuantity', e.target.value)
                                                                }
                                                            />
                                                        </TableCell>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>{row?.machines}</TableCell>
                                                <TableCell>{row?.availableQuantity}</TableCell>
                                                <TableCell>{row?.rawMaterialQuantity}</TableCell>
                                            </>
                                        )}

                                        {user?.role == 'super-admin' && (
                                            <>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => {
                                                            setProduct(row);
                                                            setShowEditModal(true);
                                                        }}
                                                        aria-label="Edit"
                                                        size="small"
                                                    >
                                                        <BorderColorIcon sx={{ color: '#1976d2' }} />
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => {
                                                            setProduct(row);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        aria-label="Delete"
                                                        size="small"
                                                    >
                                                        <DeleteIcon sx={{ color: 'red' }} />
                                                    </IconButton>
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />

                {!!error && <ErrorScreen error={error} />}
            </>

            {showCreateModal && (
                <ModalCustom open={showCreateModal} title={'Create Product'}>
                    <AddNew onClose={() => setShowCreateModal(false)} />
                </ModalCustom>
            )}

            {showEditModal && (
                <ModalCustom open={showEditModal} title={'Edit Product'}>
                    <Edit product={product} onClose={() => setShowEditModal(false)} />
                </ModalCustom>
            )}

            <ModalConfirmation
                open={showDeleteModal}
                loading={loadingDelReq}
                header={'Delete Product'}
                description={`Are you sure, you want to delete ${product?.name}?`}
                onClickNo={() => setShowDeleteModal(false)}
                onClickYes={handleDelete}
            />

            <div style={{ display: 'none' }}>
                <ProductPrint ref={componentRef} data={filteredData || []} />
            </div>
        </Layout>
    );
}
